import type { RunRuntime } from '../types';

/**
 * Decode the timestamp baked into the `build` string by the agent-core CDK
 * synth step. `build` is shaped `<short-git-sha>-<base36 unix-seconds>`,
 * e.g. `"000403a-tf7yug"`. The base36 segment is `Math.floor(Date.now()/1000).toString(36)`.
 *
 * Returns `null` for the literal `"nogit"` placeholder, malformed input, or
 * unparseable timestamps so the caller can branch on a single negative test.
 */
export function parseBuildTime(build: string): Date | null {
  const dash = build.indexOf('-');
  if (dash === -1) return null;
  const ts = build.slice(dash + 1);
  if (!ts) return null;
  const seconds = parseInt(ts, 36);
  if (!Number.isFinite(seconds) || seconds <= 0) return null;
  return new Date(seconds * 1000);
}

/**
 * Narrow `runtime` to the case where both build-identity fields are present.
 * Avoids the "v · " empty footer when the backend returns a partial runtime
 * map (e.g. only `sessionId`, no version/build).
 */
export function hasBuildInfo(
  info: RunRuntime | undefined,
): info is RunRuntime & { version: string; build: string } {
  return !!info && !!info.version && !!info.build;
}
